import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, LineChart, FileSpreadsheet, ShieldCheck, Wrench, Zap, Building2, Play, Users, HardHat, Factory } from "lucide-react";
import PercorsoOperativo from "@/components/PercorsoOperativo";
import CosaAgevolabile from "@/components/CosaAgevolabile";
import PuntiForza from "@/components/PuntiForza";
import NewsletterWidget from "@/components/NewsletterWidget";
import SocialShare from "@/components/SocialShare";

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="dark-hero relative min-h-[80vh] flex items-center">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 border border-brand/40 rounded-full text-[11px] font-display uppercase tracking-[0.2em] text-brand"
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
              Legge 30/12/2025 n. 199 · Aggiornato
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02]"
              data-testid="hero-title"
            >
              Calcolatore<br />
              <span className="text-brand">Iperammortamento</span><br />
              2026
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-6 max-w-2xl text-lg text-white/70 leading-relaxed"
            >
              Il tool di <img src="https://customer-assets.emergentagent.com/job_iper-ammortamento/artifacts/jputfjcg_Studio-ingegneria-logb%20%282%29%20%281%29.png" alt="Studio Ingegneria Valore" className="inline-block h-8 w-auto align-middle mx-1" /> per stimare in modo puntuale i benefici fiscali dell'iperammortamento su investimenti in beni Industria 4.0. Scaglioni, Nuova Sabatini, piano pluriennale, export PDF ed Excel.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <Link
                to="/calcolatore"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 px-6 h-12 bg-brand text-white rounded font-display uppercase tracking-widest text-sm hover:bg-brand-dark hover:shadow-brand-glow hover:-translate-y-[1px]"
              >
                Apri il calcolatore
                <ArrowRight className="w-4 h-4 transition group-hover:translate-x-0.5" />
              </Link>
              <a
                href="https://www.studioingegneriavalore.it/contatti/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-6 h-12 border border-white/25 text-white rounded font-display uppercase tracking-widest text-sm hover:bg-white/10"
              >
                <Play className="w-4 h-4" /> Contattaci
              </a>
            </motion.div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-2xl">
              <Stat label="Fino a 10M€" value="70%" />
              <Stat label="10M€ – 20M€" value="50%" />
              <Stat label="Legge di riferimento" value="199/25" />
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <MockCard />
          </div>
        </div>
      </section>

      {/* FEATURE TILES DARK (like SIV homepage after hero) */}
      <section className="bg-bg-dark text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <IconFeature
            icon={<Building2 className="w-8 h-8" />}
            title="Progettazione"
            desc="Analisi tecnica dell'investimento, requisiti oggettivi e soggettivi Industria 5.0."
          />
          <IconFeature
            icon={<Zap className="w-8 h-8" />}
            title="Perizie 5.0"
            desc="Perizie per usufruire del beneficio fiscale della nuova legge di bilancio sul tuo impianto produttivo."
          />
          <IconFeature
            icon={<Wrench className="w-8 h-8" />}
            title="Analisi termotecnica"
            desc="Valutazione dei dati termo-igrometrici e progettazione di impianti termici efficienti."
          />
        </div>
      </section>

      {/* I NOSTRI NUMERI */}
      <section className="bg-white border-y border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-display tracking-[0.3em] uppercase text-brand">Track record</div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl text-ink">
              I nostri <span className="text-brand">numeri</span>
            </h2>
            <p className="mt-4 text-ink-muted text-sm">
              Anni di consulenza al fianco delle imprese italiane per accedere agli incentivi fiscali
              su investimenti produttivi, sicurezza e transizione tecnologica.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6"
            data-testid="numeri-section"
          >
            <StatCard
              icon={<Users className="w-6 h-6" />}
              prefix="+"
              value="1.500"
              suffix=""
              label="Clienti seguiti"
              caption="Imprese assistite nella progettazione e nella pratica"
            />
            <StatCard
              icon={<HardHat className="w-6 h-6" />}
              prefix="+"
              value="4,5"
              suffix="MLN€"
              label="Bando ISI INAIL"
              caption="Contributi erogati per sicurezza sul lavoro e impianti"
            />
            <StatCard
              icon={<Factory className="w-6 h-6" />}
              prefix="+"
              value="8"
              suffix="MLN€"
              label="Iperammortamento 2026 · Nuova Sabatini"
              caption="Beneficio fiscale ottenuto sui piani di investimento"
            />
          </motion.div>
        </div>
      </section>

      {/* PUNTI DI FORZA — success fee al centro */}
      <PuntiForza variant="full" />

      {/* SERVIZI SECTION */}
      <section className="pattern-bg py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs font-display tracking-[0.3em] uppercase text-ink-muted">Una valutazione su misura con un professionista qualificato</div>
          <h2 className="mt-4 font-display text-5xl sm:text-6xl text-ink">Il nostro <span className="text-brand">calcolatore</span></h2>
          <p className="mt-6 max-w-2xl mx-auto text-ink-muted">
            Progettazione impiantistica · Analisi energetica · Progettazione edilizia · Perizie 5.0 · Sicurezza cantieri
          </p>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <Feature
              icon={<LineChart className="w-5 h-5" />}
              title="Piano pluriennale"
              desc="Ripartizione anno per anno di ammortamento ordinario, iperammortamento e vantaggio fiscale con grafico dedicato."
            />
            <Feature
              icon={<FileSpreadsheet className="w-5 h-5" />}
              title="Export PDF & Excel"
              desc="Scarica il dettaglio della simulazione in un file pronto da condividere con clienti, banche o commercialista."
            />
            <Feature
              icon={<ShieldCheck className="w-5 h-5" />}
              title="Storico locale"
              desc="Salva ogni simulazione direttamente nel browser: nessun account, nessun dato lascia il tuo dispositivo."
            />
          </div>

          <div className="mt-16">
            <div className="text-6xl font-display text-brand">33<span className="align-super text-3xl">%</span></div>
            <div className="mt-2 text-xs font-display tracking-[0.25em] uppercase text-ink-muted">
              Sostenibilità ambientale · Innovazione tecnologica · Efficienza energetica
            </div>
          </div>
        </div>
      </section>

      {/* CTA DARK */}
      <CosaAgevolabile />
      <PercorsoOperativo />

      <section className="dark-hero py-24">
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs font-display tracking-[0.3em] uppercase text-brand">Investi con consapevolezza</div>
          <h2 className="mt-4 font-display text-5xl sm:text-6xl text-white">
            Simula in <span className="text-brand">3 minuti</span>.
          </h2>
          <p className="mt-6 text-lg text-white/70">
            Inserisci l'importo del bene, la tipologia di investimento e ottieni all'istante il quadro completo del beneficio fiscale.
          </p>
          <Link
            to="/calcolatore"
            data-testid="cta-open-calc"
            className="mt-10 inline-flex items-center gap-2 px-8 h-14 bg-brand text-white rounded font-display uppercase tracking-widest text-sm hover:bg-brand-dark hover:shadow-brand-glow"
          >
            Prova adesso <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-bg-dark text-white/70 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="font-display text-white text-lg">Iperammortamento Valore</div>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Il calcolatore ufficiale di <a href="https://www.studioingegneriavalore.it" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">Studio Ingegneria Valore</a> per stimare i benefici fiscali dell'iperammortamento e della Nuova Sabatini.
            </p>
            <p className="mt-3 text-xs text-white/50 leading-relaxed">
              Via Ca' Brusà 16 · 37030 Lavagno (VR)<br />
              <a href="mailto:info@iperammortamentovalore.com" className="text-brand hover:underline">info@iperammortamentovalore.com</a>
            </p>
          </div>
          <div>
            <div className="font-display text-white text-sm uppercase tracking-widest mb-3">Naviga</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/calcolatore" className="hover:text-white">Calcolatore</Link></li>
              <li><Link to="/contattaci" className="hover:text-white">Contattaci</Link></li>
              <li><Link to="/privacy" className="hover:text-white">Informativa Privacy</Link></li>
            </ul>
          </div>
          <div>
            <NewsletterWidget />
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs text-white/50">© {new Date().getFullYear()} Studio Ingegneria Valore · Tutti i diritti riservati</div>
            <SocialShare />
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 border-brand/60 pl-4">
      <div className="font-display text-[10px] uppercase tracking-[0.25em] text-white/50">{label}</div>
      <div className="font-display text-3xl text-white mt-1">{value}</div>
    </div>
  );
}

function StatCard({ icon, prefix, value, suffix, label, caption }) {
  return (
    <div className="group relative bg-white border border-line rounded-lg p-8 shadow-card hover:border-brand hover:shadow-lg transition text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand/10 text-brand group-hover:bg-brand group-hover:text-white transition">
        {icon}
      </div>
      <div className="mt-6 font-display text-5xl sm:text-6xl text-ink leading-none tracking-tight">
        <span className="text-brand">{prefix}</span>
        {value}
        {suffix && <span className="text-2xl sm:text-3xl align-baseline ml-1 text-brand">{suffix}</span>}
      </div>
      <div className="mt-4 font-display text-[11px] tracking-[0.25em] uppercase text-ink">{label}</div>
      <p className="mt-2 text-sm text-ink-muted">{caption}</p>
    </div>
  );
}

function IconFeature({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-14 h-14 flex items-center justify-center bg-brand/10 text-brand rounded">
        {icon}
      </div>
      <div>
        <h3 className="font-display text-lg text-white">{title}</h3>
        <p className="mt-2 text-sm text-white/60 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="bg-white border border-line rounded-md p-6 hover:shadow-card-hover hover:border-brand/40 transition">
      <div className="w-10 h-10 rounded bg-brand/10 text-brand flex items-center justify-center">{icon}</div>
      <h3 className="mt-4 font-display text-lg text-ink">{title}</h3>
      <p className="mt-2 text-sm text-ink-muted leading-relaxed">{desc}</p>
    </div>
  );
}

function MockCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-white/[0.04] border border-white/10 backdrop-blur-md rounded p-6 relative"
      data-testid="hero-mock"
    >
      <div className="flex items-center justify-between border-b border-white/10 pb-3">
        <div>
          <div className="font-display text-[10px] uppercase tracking-[0.25em] text-white/50">Simulazione</div>
          <div className="font-display text-white mt-1">Bene 4.0 · € 500.000</div>
        </div>
        <span className="font-mono text-[10px] text-brand uppercase">2026</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <MockStat label="Maggiorazione" value="€ 900.000" />
        <MockStat label="Beneficio IRES" value="€ 216.000" accent />
        <MockStat label="% investimento" value="43,20%" />
        <MockStat label="Investimento netto" value="€ 284.000" />
      </div>
      <div className="mt-5 h-[70px] flex items-end gap-1.5">
        {[15, 40, 55, 62, 48, 30, 22, 15, 10].map((h, i) => (
          <div key={i} className="flex-1 rounded-sm" style={{ height: `${h}px`, background: i === 3 ? "#1AAB67" : "rgba(255,255,255,0.35)" }} />
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-white/40">
        <span>2026</span>
        <span>vantaggio annuo</span>
        <span>2034</span>
      </div>
    </motion.div>
  );
}

function MockStat({ label, value, accent }) {
  return (
    <div>
      <div className="font-display text-[9px] uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className={`font-mono text-base mt-0.5 ${accent ? "text-brand font-semibold" : "text-white"}`}>{value}</div>
    </div>
  );
}
